import React, { useCallback, useState } from 'react';
import apiConfig from '../../../config/api';
import MatrixGrid from '../components/MatrixGrid';
import { makeScenario, SCENARIO_GOALS, STATUS } from '../state/model';
import { cellKey, emptyCell, makeRow, makeColumn, AREA_CLASS, classTotals, grandTotal } from '../state/matrix';
import { computeProgramFit } from '../engine/program-fit';
import { fmt } from '../engine/rights-engine';
import { buildMatrixFromProgram } from '../engine/scenario-builder';
import { useDialog } from '../components/Dialog';

// מצב מוצע — scenario builder. Each scenario owns its own proposed-state matrix
// (same shape as existing, for direct comparison), a developer program, and a
// live program-fit table (what fits / what doesn't / what needs another track).
export default function ProposedStateTab({ project, updateProject }) {
  const dlg = useDialog();
  const [translating, setTranslating] = useState(false);
  const [translateError, setTranslateError] = useState(null);

  const scenarios = project.scenarios;
  const active = scenarios.find((s) => s.id === project.activeScenarioId) || scenarios[0] || null;

  const createScenario = useCallback(() => {
    updateProject((p) => {
      const s = makeScenario({ name: `תרחיש ${p.scenarios.length + 1}` });
      p.scenarios.push(s);
      p.activeScenarioId = s.id;
    }, 'נוצר תרחיש');
  }, [updateProject]);

  const selectScenario = useCallback((id) => {
    updateProject((p) => { p.activeScenarioId = id; });
  }, [updateProject]);

  const deleteScenario = useCallback((id) => {
    updateProject((p) => {
      p.scenarios = p.scenarios.filter((s) => s.id !== id);
      if (p.activeScenarioId === id) p.activeScenarioId = p.scenarios[0]?.id || null;
    }, 'נמחק תרחיש');
  }, [updateProject]);

  // mutate the active scenario
  const mutateActive = useCallback((mutator, audit) => {
    updateProject((p) => {
      const s = p.scenarios.find((x) => x.id === (p.activeScenarioId || p.scenarios[0]?.id));
      if (s) mutator(s);
    }, audit);
  }, [updateProject]);

  const setName = (v) => mutateActive((s) => { s.name = v; });
  const setGoal = (v) => mutateActive((s) => { s.goal = v; }, 'עודכנה מטרת תרחיש');
  const setFreeText = (v) => mutateActive((s) => { s.program.freeText = v; });
  const setTargetUnits = (v) => mutateActive((s) => { s.program.targetUnits = v === '' ? null : Number(v); }, 'עודכן יעד יח"ד');

  // matrix handlers (scoped to active scenario)
  const setCell = (rowId, colId, sqm) => mutateActive((s) => {
    const k = cellKey(rowId, colId);
    s.matrix.cells[k] = { ...(s.matrix.cells[k] || emptyCell()), sqm, status: sqm == null ? STATUS.MISSING : STATUS.MANUAL };
  }, 'עודכן תא במטריצת מצב מוצע');
  const setCellStatus = (rowId, colId, status) => mutateActive((s) => {
    const k = cellKey(rowId, colId);
    if (s.matrix.cells[k]) s.matrix.cells[k].status = status;
  });
  const addRow = (label) => mutateActive((s) => { s.matrix.rows.push(makeRow({ label })); }, 'נוסף מפלס');
  const addColumn = (label) => mutateActive((s) => {
    s.matrix.columns.push(makeColumn(`c${s.matrix.columns.length}`, label, AREA_CLASS.UNCOUNTED));
  }, 'נוסף רכיב');
  const setColClass = (colId, classification) => mutateActive((s) => {
    const col = s.matrix.columns.find((c) => c.id === colId);
    if (col) col.classification = classification;
  });

  // AI: translate free-text program → structured params
  const translateProgram = useCallback(async () => {
    if (!active?.program.freeText.trim()) return;
    setTranslateError(null);
    setTranslating(true);
    try {
      const resp = await fetch(`${apiConfig.apiBaseUrl}/api/ask?action=rights-program-parse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ freeText: active.program.freeText }),
      });
      if (!resp.ok) throw new Error(`server ${resp.status}`);
      const data = await resp.json();
      mutateActive((s) => {
        s.program.params = (data.params || []).map((pr) => ({ ...pr, source: 'AI' }));
        // 0 is never a meaningful target — "maximize" intents must stay null.
        if (Number(data.target_units) > 0) s.program.targetUnits = Number(data.target_units);
      }, 'פרוגרמה תורגמה לפרמטרים (AI)');
    } catch (e) {
      setTranslateError('תרגום הפרוגרמה נכשל. נסה שוב.');
    } finally {
      setTranslating(false);
    }
  }, [active, mutateActive]);

  // Deterministic generation: program params + rights envelope → draft matrix.
  const generateMatrix = useCallback(async () => {
    if (!active) return;
    const res = buildMatrixFromProgram(project, active);
    if (res.error) { setTranslateError(res.error); return; }
    if (grandTotal(active.matrix) > 0) {
      const ok = await dlg.confirm({
        title: 'בניית מטריצה מהפרוגרמה',
        message: 'המטריצה הקיימת בתרחיש תוחלף בטיוטה שנבנתה מהפרוגרמה. להמשיך?',
        confirmLabel: 'בנה מחדש',
        cancelLabel: 'ביטול',
        danger: true,
      });
      if (!ok) return;
    }
    setTranslateError(null);
    mutateActive((s) => {
      s.matrix = res.matrix;
      s.program.generated = { assumptions: res.assumptions, estimatedUnits: res.estimatedUnits };
      if (s.program.targetUnits == null && res.estimatedUnits > 0) s.program.targetUnits = res.estimatedUnits;
    }, 'נבנתה מטריצת מצב מוצע מהפרוגרמה');
  }, [active, project, mutateActive, dlg]);

  if (!active) {
    return (
      <div className="rd-card rd-placeholder">
        <h3 className="rd-card-title">מצב מוצע</h3>
        <p className="rd-card-sub">בניית תרחישי מצב מוצע לפי זכויות, מגבלות ופרוגרמת יזם.</p>
        <button type="button" className="rd-autofill-btn" onClick={createScenario}>+ צור תרחיש ראשון</button>
      </div>
    );
  }

  const fit = computeProgramFit(project, active);
  // Same fallback the rights engine uses: the classified matrix when filled,
  // else the as-built summary total (the matrix starts empty by design).
  const existingTotals = classTotals(project.existingState.matrix);
  const matrixArea = existingTotals.main + existingTotals.service;
  const summaryBuilt = Number(project.existingState.summary?.builtArea?.value);
  const existingArea = matrixArea > 0 ? matrixArea : (Number.isFinite(summaryBuilt) ? summaryBuilt : 0);
  const existingFromSummary = matrixArea === 0 && existingArea > 0;

  return (
    <div className="rd-proposed-tab">
      {/* ---- Scenario tabs ------------------------------------------------- */}
      <div className="rd-scenario-bar">
        {scenarios.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`rd-scenario-chip ${s.id === active.id ? 'active' : ''}`}
            onClick={() => selectScenario(s.id)}
          >
            {s.name}
          </button>
        ))}
        <button type="button" className="rd-mini-btn" onClick={createScenario}>+ תרחיש</button>
      </div>

      {/* ---- Scenario header ---------------------------------------------- */}
      <section className="rd-card">
        <div className="rd-scenario-head">
          <input className="rd-input rd-scenario-name" value={active.name} onChange={(e) => setName(e.target.value)} />
          <select className="rd-input rd-input-sm" value={active.goal} onChange={(e) => setGoal(e.target.value)}>
            {SCENARIO_GOALS.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
          </select>
          {scenarios.length > 1 && (
            <button type="button" className="rd-remove-btn" onClick={() => deleteScenario(active.id)} title="מחק תרחיש">✕ מחק</button>
          )}
        </div>
      </section>

      {/* ---- Developer program -------------------------------------------- */}
      <section className="rd-card">
        <h3 className="rd-card-title">פרוגרמת יזם</h3>
        <p className="rd-card-sub">תאר את הפרוגרמה בשפה חופשית — ה-AI יתרגם לפרמטרים שתאשר. לדוגמה: "מקסימום דירות, 2 דירות בקומה, תוספת 15 מ"ר לכל דירה קיימת, בלי עיקרי בקרקע".</p>
        <textarea
          className="rd-input rd-textarea"
          value={active.program.freeText}
          placeholder="פרוגרמה בשפה חופשית..."
          onChange={(e) => setFreeText(e.target.value)}
        />
        <div className="rd-program-actions">
          <button type="button" className="rd-autofill-btn" onClick={translateProgram} disabled={translating || !active.program.freeText.trim()}>
            {translating ? 'מתרגם…' : 'תרגם לפרמטרים (AI) ✦'}
          </button>
          <button type="button" className="rd-mini-btn rd-generate-btn" onClick={generateMatrix} title="בניית טיוטת מטריצה דטרמיניסטית מהפרמטרים ומעטפת הזכויות">
            בנה מטריצה מהפרוגרמה ⚙
          </button>
          <label className="rd-target-units">
            יח"ד יעד:
            <input className="rd-input rd-input-sm" type="number" value={active.program.targetUnits ?? ''} onChange={(e) => setTargetUnits(e.target.value)} />
          </label>
        </div>
        {translateError && <div className="rd-error">{translateError}</div>}
        {active.program.params.length > 0 && (
          <table className="rd-table rd-params-table">
            <thead><tr><th>פרמטר</th><th>ערך</th><th>מקור</th></tr></thead>
            <tbody>
              {active.program.params.map((pr, i) => (
                <tr key={i}>
                  <td>{pr.label || pr.key}</td>
                  <td>{String(pr.value)}</td>
                  <td>{pr.source || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* ---- Proposed matrix ---------------------------------------------- */}
      <section className="rd-card">
        <h3 className="rd-card-title">מטריצת מצב מוצע</h3>
        <p className="rd-card-sub">אותה שפה כמו מטריצת מצב קיים — לצורך השוואה ישירה. כל תא נשען על פרמטרים שניתן לראות ולערוך.</p>
        <MatrixGrid
          matrix={active.matrix}
          onCellChange={setCell}
          onSetCellStatus={setCellStatus}
          onAddRow={addRow}
          onAddColumn={addColumn}
          onColClassChange={setColClass}
        />
        {active.program.generated?.assumptions?.length > 0 && (
          <div className="rd-generated-assumptions">
            <div className="rd-generated-title">הנחות הטיוטה — כל התאים מסומנים "משוער" וניתנים לעריכה:</div>
            <ul>
              {active.program.generated.assumptions.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          </div>
        )}
      </section>

      {/* ---- Existing vs proposed (bars) ----------------------------------- */}
      <section className="rd-card">
        <h3 className="rd-card-title" style={{ marginBottom: 16 }}>קיים מול מוצע</h3>
        <div className="rd-compare">
          {(() => {
            const max = Math.max(existingArea, fit.proposedArea, 1);
            const delta = fit.proposedArea - existingArea;
            return (
              <>
                <div className="rd-bar-row">
                  <div className="rd-bar-head">
                    <span className="lbl">{existingFromSummary ? 'קיים (מתוך סיכום מצב בנוי)' : 'קיים (עיקרי+שירות)'}</span>
                    <span className="val">{fmt(existingArea)} מ"ר</span>
                  </div>
                  <div className="rd-bar-track"><div className="rd-bar-fill" style={{ width: `${(existingArea / max) * 100}%` }} /></div>
                </div>
                <div className="rd-bar-row">
                  <div className="rd-bar-head">
                    <span className="lbl">מוצע</span>
                    <span className="val proposed">{fmt(fit.proposedArea)} מ"ר</span>
                  </div>
                  <div className="rd-bar-track"><div className="rd-bar-fill proposed" style={{ width: `${(fit.proposedArea / max) * 100}%` }} /></div>
                </div>
                <div className="rd-compare-delta-row">
                  <span className={`rd-compare-delta ${delta <= 0 ? 'pos' : 'neg'}`}>{delta >= 0 ? '+' : ''}{fmt(delta)} מ"ר</span>
                  <span className="rd-compare-note">{delta <= 0 ? 'המוצע קטן מהקיים' : 'המוצע גדול מהקיים'}</span>
                </div>
              </>
            );
          })()}
        </div>
      </section>

      {/* ---- Program fit -------------------------------------------------- */}
      <section className="rd-card">
        <h3 className="rd-card-title">מה נכנס / מה לא נכנס</h3>
        <p className="rd-card-sub">השוואת הפרוגרמה מול מעטפת הזכויות. הבקשה המקורית נשמרת גם כשאינה נכנסת.</p>
        <table className="rd-table">
          <thead>
            <tr><th>רכיב</th><th>ביקש</th><th>נכנס לפי זכויות</th><th>לא נכנס</th><th>הערה</th></tr>
          </thead>
          <tbody>
            {fit.rows.map((r) => (
              <tr key={r.key}>
                <td>{r.label}</td>
                <td>{fmt(r.requested)}</td>
                <td>{r.missing ? '—' : fmt(r.fits)}</td>
                <td className={!r.missing && r.doesntFit > 0 ? 'rd-overflow' : ''}>{r.missing ? '—' : fmt(r.doesntFit)}</td>
                <td className={r.missing ? 'rd-metric-missing' : (r.doesntFit > 0 ? 'rd-overflow' : 'rd-fits')}>{r.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
